import { Injectable } from '@angular/core'
import { Firestore, doc, getDoc, setDoc } from '@angular/fire/firestore'
import { GlobalVariables, globalVariablesPath } from '@parkupp/core'
import { BehaviorSubject, Observable } from 'rxjs'

@Injectable({
    providedIn: 'root',
})
export class GlobalVariablesService {
    private globalVariables$ = new BehaviorSubject<GlobalVariables>(new GlobalVariables())
    private loaded = false

    constructor(private firestore: Firestore) {
        this.loadGlobalVariables()
    }

    get globalVariables(): Observable<GlobalVariables> {
        return this.globalVariables$.asObservable()
    }

    get currentVariables(): GlobalVariables {
        return this.globalVariables$.value
    }

    async loadGlobalVariables(): Promise<GlobalVariables> {
        try {
            const docRef = doc(this.firestore, globalVariablesPath())
            const docSnap = await getDoc(docRef)

            if (docSnap.exists()) {
                const data = docSnap.data()
                const variables = new GlobalVariables(data)
                this.globalVariables$.next(variables)
                this.loaded = true
                return variables
            } else {
                // Create default values if document doesn't exist
                const defaultVariables = new GlobalVariables()
                await this.saveGlobalVariables(defaultVariables)
                this.loaded = true
                return defaultVariables
            }
        } catch (error) {
            console.error('Error loading global variables:', error)
            // Return defaults on error
            const defaultVariables = new GlobalVariables()
            this.globalVariables$.next(defaultVariables)
            return defaultVariables
        }
    }

    async saveGlobalVariables(variables: GlobalVariables, updatedBy?: string): Promise<void> {
        try {
            const docRef = doc(this.firestore, globalVariablesPath())

            const dataToSave = {
                profitPercentage: variables.profitPercentage,
                averageBayPrice: variables.averageBayPrice,
                averageMonths: variables.averageMonths,
                averageBaysPerSub: variables.averageBaysPerSub,
                updatedAt: new Date(),
                updatedBy: updatedBy || 'system',
            }

            await setDoc(docRef, dataToSave, { merge: true })

            // Update the local state
            const updatedVariables = new GlobalVariables(dataToSave)
            this.globalVariables$.next(updatedVariables)
        } catch (error) {
            console.error('Error saving global variables:', error)
            throw error
        }
    }

    // Helper methods for calculating lead value
    calculateLeadValue(bays: number, months: number): number {
        const vars = this.currentVariables
        return vars.averageBayPrice * (bays || vars.averageBaysPerSub) * (months || vars.averageMonths)
    }

    getLeadValueTier(bays: number, months: number): 'HIGH' | 'MEDIUM' | 'LOW' {
        const vars = this.currentVariables
        const leadValue = this.calculateLeadValue(bays, months)
        const avgValue = vars.averageBayPrice * vars.averageMonths * vars.averageBaysPerSub

        if (leadValue >= avgValue * 2) {
            return 'HIGH'
        } else if (leadValue <= avgValue * 0.5) {
            return 'LOW'
        } else {
            return 'MEDIUM'
        }
    }

    getLeadValueAmount(bays: number, months: number): number {
        return this.calculateLeadValue(bays, months)
    }
}

